import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { environment } from '../config/environment';
import { getApiHeadersForGet } from '../utils/apiUtils';

interface SeoMetadata {
  title?: string | null;
  description?: string | null;
  keywords?: string | null;
  image_path?: string | null;
  image_base64?: string | null;
}

interface UseSeoMetadataOptions {
  pageTitle?: string;
  pageDescription?: string;
  pageKeywords?: string;
  useGlobalSeo?: boolean;
}

/**
 * Hook para inyectar meta tags SEO dinámicamente en el HTML
 * Prioridad: page-specific > global SEO config
 */
export const useSeoMetadata = (options: UseSeoMetadataOptions = {}) => {
  const {
    pageTitle,
    pageDescription,
    pageKeywords,
    useGlobalSeo = true
  } = options;
  
  // Obtener la ubicación actual para que el hook se ejecute en cada cambio de ruta
  const location = useLocation();

  useEffect(() => {
    // Función helper para actualizar o crear meta tag
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Función para aplicar SEO metadata
    const applySeoMetadata = (globalSeo: SeoMetadata | null = null) => {
      // Determinar valores finales con prioridad: page-specific > global SEO > defaults
      const finalTitle = pageTitle || 
                        globalSeo?.title || 
                        'Pet contest';
      
      const finalDescription = pageDescription || 
                              globalSeo?.description || 
                              'Sistema de gestión de concursos de mascotas';
      
      const finalKeywords = pageKeywords || 
                           globalSeo?.keywords || 
                           'pet contest, concursos, mascotas';

      // Actualizar título de la página
      document.title = finalTitle;

      // Actualizar meta title
      updateMetaTag('title', finalTitle);

      // Actualizar meta description
      updateMetaTag('description', finalDescription);

      // Actualizar meta keywords
      updateMetaTag('keywords', finalKeywords);

      // Actualizar meta image si está disponible
      const finalImage = globalSeo?.image_base64 || globalSeo?.image_path || null;
      if (finalImage) {
        // Si es base64, usarlo directamente; si es path, convertirlo a URL completa
        const imageUrl = finalImage.startsWith('data:image') 
          ? finalImage 
          : (finalImage.startsWith('http') 
              ? finalImage 
              : `${window.location.origin}${finalImage.startsWith('/') ? '' : '/'}${finalImage}`);
        updateMetaTag('image', imageUrl);
      }
    };

    // Cargar configuración SEO global si está habilitado
    if (useGlobalSeo) {
      // Usar un timestamp para evitar caché
      fetch(`${environment.apiBaseUrl}/seo-config?t=${Date.now()}`, {
        headers: getApiHeadersForGet()
      })
        .then(res => res.json())
        .then(data => {
          const globalSeo: SeoMetadata | null = data || null;
          applySeoMetadata(globalSeo);
        })
        .catch(err => {
          console.warn('Error loading global SEO config:', err);
          // Aplicar con valores por defecto si falla la carga
          applySeoMetadata(null);
        });
    } else {
      // Aplicar sin cargar configuración global
      applySeoMetadata(null);
    }
  }, [pageTitle, pageDescription, pageKeywords, useGlobalSeo, location.pathname]);
};

export default useSeoMetadata;

